import type { Phrase, Folder, PhraseSection, PrimaryTag } from './types';
import { updatedDate } from './freshness';
import { mapConcurrent } from './mapConcurrent';

/**
 * Adapter that turns the bundled markdown library (src/markdown/**.md) into the
 * Phrase/Folder shapes the UI consumes. This is the Phase 5 data source fed to
 * PhrasesProvider via its `source` prop.
 *
 * Top-level folder name === clinical category (matches FOLDER_OPTIONS and the
 * PrimaryTag union). Examination has subfolders (exam/proc/notes) surfaced as
 * folder children.
 */

type FolderMeta = { kind: PrimaryTag; label: string; color: string };

const FOLDER_META: Record<string, FolderMeta> = {
  inpatient: { kind: 'inpatient', label: 'Inpatient', color: 'var(--tag-inpatient-fg)' },
  outpatient: { kind: 'outpatient', label: 'Outpatient', color: 'var(--tag-outpatient-fg)' },
  icu: { kind: 'icu', label: 'ICU', color: 'var(--tag-icu-fg)' },
  ed: { kind: 'ed', label: 'ED', color: 'var(--tag-ed-fg)' },
  examination: { kind: 'exam', label: 'Examination', color: 'var(--tag-exam-fg)' },
  protocols: { kind: 'protocols', label: 'Protocols', color: 'var(--tag-protocols-fg)' },
  references: { kind: 'references', label: 'References', color: 'var(--tag-references-fg)' },
};

const FOLDER_ORDER = ['inpatient', 'outpatient', 'icu', 'ed', 'examination', 'protocols', 'references'];

// Medical acronyms/abbreviations that must not be naively title-cased. Notes
// without an explicit `# heading` derive their title from the filename via
// humanize(), so without this map ".tia"/".cabg-workup"/".nstemi" would render
// as "Tia"/"Cabg Workup"/"Nstemi". Keyed by the lowercase slug token; the value
// is the exact display form (mostly all-caps, a few standard mixed-case forms).
// Add a new entry here whenever a note filename introduces another acronym.
const ACRONYMS: Record<string, string> = {
  tia: 'TIA', nstemi: 'NSTEMI', stemi: 'STEMI', mi: 'MI', cabg: 'CABG',
  copd: 'COPD', ckd: 'CKD', esrd: 'ESRD', dka: 'DKA', hhs: 'HHS', gi: 'GI',
  dvt: 'DVT', pe: 'PE', aki: 'AKI', gerd: 'GERD', ibs: 'IBS', dic: 'DIC',
  hiv: 'HIV', tb: 'TB', tbi: 'TBI', uti: 'UTI', osa: 'OSA', bph: 'BPH',
  ards: 'ARDS', sirs: 'SIRS', vt: 'VT', nsvt: 'NSVT', bppv: 'BPPV',
  dress: 'DRESS', adhd: 'ADHD', sle: 'SLE', psa: 'PSA', hcv: 'HCV',
  gvhd: 'GVHD', sjs: 'SJS', ten: 'TEN', svc: 'SVC', sti: 'STI', urti: 'URTI',
  gbs: 'GBS', masld: 'MASLD', icans: 'ICANS', icp: 'ICP', ild: 'ILD',
  inr: 'INR', ip: 'IP', pcos: 'PCOS', ppd: 'PPD', pres: 'PRES', hpi: 'HPI',
  mva: 'MVA', pd: 'PD', covid: 'COVID', ent: 'ENT',
  icu: 'ICU',
  igg4: 'IgG4', qtc: 'QTc', subq: 'SubQ', ap: 'A&P', fu: 'Follow-Up',
};

function humanize(slug: string): string {
  return slug
    .replace(/[-_]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => ACRONYMS[w.toLowerCase()] ?? w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function slugify(input: string): string {
  return input.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

/** Strip ``` code-fence markers so bodies stay copy-clean plain text. */
function stripFences(text: string): string {
  return text
    .split('\n')
    .filter((l) => !/^\s*```/.test(l))
    .join('\n');
}

function deriveTitle(content: string, fallback: string): string {
  const first = content.split('\n').find((l) => l.trim().length > 0) ?? '';
  const m = first.match(/^#\s+(.+)/);
  return (m ? m[1] : fallback).trim();
}

function deriveSummary(content: string): string {
  const lines = content.split('\n');
  const start = /^#\s+/.test(lines[0] ?? '') ? 1 : 0;
  const text = stripFences(lines.slice(start).join('\n'))
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean)
    .join(' ')
    .trim();
  if (text.length <= 160) return text;
  return text.slice(0, 159).trimEnd() + '…';
}

/** Strip inline markdown markers (**bold**, `code`) out of a heading. */
function cleanHeading(s: string): string {
  return s.replace(/[*`]/g, '').trim();
}

function parseSections(content: string, name: string): PhraseSection[] {
  const lines = content.split('\n');
  const start = /^#\s+/.test(lines[0] ?? '') ? 1 : 0;
  const rest = lines.slice(start);

  const sections: PhraseSection[] = [];
  let curTitle = '';
  let buf: string[] = [];

  const flush = () => {
    const raw = buf.join('\n').trim();      // markdown intact — for rendering
    const body = stripFences(raw).trim();   // copy-clean plain text — for copy + search
    if (body) sections.push({ title: cleanHeading(curTitle) || name, body, raw });
    buf = [];
  };

  for (const line of rest) {
    const h = line.match(/^(#{2,3})\s+(.+)/);
    if (h) {
      flush();
      curTitle = h[2].trim();
    } else {
      buf.push(line);
    }
  }
  flush();

  if (sections.length === 0) {
    const raw = rest.join('\n').trim();
    const body = stripFences(raw).trim();
    sections.push({ title: name, body: body || name, raw: raw || name });
  }
  return sections;
}

type Loaded = { phrases: Phrase[]; folders: Folder[] };
let cache: Loaded | null = null;

// webpack's require.context — not described by @types/node, so type it locally.
type WebpackContext = {
  keys(): string[];
  (id: string): string;
};
type WebpackRequire = {
  context(directory: string, useSubdirectories: boolean, regExp: RegExp): WebpackContext;
};

export async function loadMarkdownPhrases(): Promise<Loaded> {
  if (cache) return cache;

  const ctx = (require as unknown as WebpackRequire).context('../markdown', true, /\.md$/);
  const raw = await mapConcurrent(
    ctx.keys(), 12, async (key) => {
      const url = ctx(key);
      const content = await fetch(url).then((r) => r.text());
      return { key, content };
    }
  );

  const phrases: Phrase[] = [];
  const topCounts: Record<string, number> = {};
  const subCounts: Record<string, Record<string, number>> = {};
  const seenIds = new Map<string, string>();

  for (const { key, content } of raw) {
    const segments = key.replace(/^\.\//, '').split('/');
    const top = segments[0];
    const meta = FOLDER_META[top];
    if (!meta) continue;

    const sub = segments.length > 2 ? segments[1] : undefined;
    const fileBase = segments[segments.length - 1].replace(/\.md$/, '');
    const relNoExt = segments.join('/').replace(/\.md$/, '');
    // ID is derived from the top-level folder + filename only (never the specialty
    // subfolder), so reorganising files into specialty subfolders keeps their IDs
    // stable — favourites and saved edits survive the move. Unique because no two
    // files share a name within a single top-level folder.
    const idPath = [top, fileBase].join('/');

    // Two files with the same name under one top-level folder collide on ID and
    // would crash the MiniSearch index. Keep the first, skip the rest loudly —
    // the fix is to rename one of the files.
    const id = slugify(idPath);
    const prior = seenIds.get(id);
    if (prior) {
      // eslint-disable-next-line no-console
      console.warn(`markdownSource: skipping ${relNoExt} — its ID "${id}" collides with ${prior}. Rename one of the files.`);
      continue;
    }
    seenIds.set(id, relNoExt);

    topCounts[top] = (topCounts[top] || 0) + 1;

    const name = deriveTitle(content, humanize(fileBase));
    const tags = [meta.label, ...(sub ? [humanize(sub)] : [])];

    phrases.push({
      id,
      name,
      trigger: '.' + fileBase.replace(/[^a-z0-9]+/gi, ''),
      tags,
      primaryTag: meta.kind,
      favorite: false,
      updated: updatedDate(relNoExt),
      summary: deriveSummary(content),
      sections: parseSections(content, name),
    });

    if (sub) {
      subCounts[top] = subCounts[top] || {};
      subCounts[top][sub] = (subCounts[top][sub] || 0) + 1;
    }
  }

  const folders: Folder[] = FOLDER_ORDER.filter((k) => FOLDER_META[k]).map((k) => {
    const meta = FOLDER_META[k];
    const subs = subCounts[k];
    const children = subs
      ? Object.keys(subs)
          .sort()
          .map((s) => ({ id: s, name: humanize(s), count: subs[s] }))
      : undefined;
    return { id: k, name: meta.label, color: meta.color, kind: meta.kind, count: topCounts[k] || 0, children };
  });

  cache = { phrases, folders };
  return cache;
}
